// eslint-disable-next-line filenames/match-exported
import React, { useMemo } from 'react';
import { MarketBanner, MarketButton } from '@market/react';
import { useHistory } from 'react-router-dom';
import ModuleLoadFailed from '../../common/errors/ModuleLoadFailed';
import ModuleLoading from '../../common/loading/ModuleLoading';
import Page from '../../layout/Page';
import { useListOrdersQuery } from 'store/query/api-extensions/orderBaseRpcs';
import { Currency } from 'rpc/model/squareup/connect/v2/common/money';
import ChartCard from './components/ChartCard';
import SummaryTiles from './components/SummaryTiles';
import HorizontalBarChart from './components/HorizontalBarChart';
import SpendOverTimeChart from './components/SpendOverTimeChart';
import UsSpendMap from './components/UsSpendMap';
import {
  dominantCurrency,
  spendByCategory,
  spendByItem,
  spendByMerchant,
  spendByZip3,
  spendOverTime,
  summarize,
} from './stats';

const PurchaseInsights: React.FC = () => {
  const history = useHistory();
  const {
    data: payments,
    isLoading,
    isError: didLoadingFail,
  } = useListOrdersQuery();

  const insights = useMemo(() => {
    const list = payments || [];
    return {
      byZip3: spendByZip3(list),
      categories: spendByCategory(list),
      currency: dominantCurrency(list),
      items: spendByItem(list),
      merchants: spendByMerchant(list),
      overTime: spendOverTime(list),
      summary: summarize(list),
    };
  }, [payments]);

  if (isLoading) {
    return <ModuleLoading />;
  }

  if (didLoadingFail) {
    return <ModuleLoadFailed />;
  }

  if (!payments || payments.length === 0) {
    return (
      <Page>
        <h2 className="heading-30">Purchase insights</h2>
        <p className="paragraph-30 text-grey">
          You don&apos;t have any completed purchases yet.
        </p>
        <MarketButton onClick={() => history.push('/purchasehistory')}>
          Back to purchase history
        </MarketButton>
      </Page>
    );
  }

  return (
    <Page>
      {/* Remove before GA */}
      <MarketBanner>
        Purchase Insights is under development and is exposed to Block employees
        only. Please reach out in{' '}
        <a href="https://square.enterprise.slack.com/archives/CJWA97E91">
          #square-profile
        </a>{' '}
        with questions or feedback.
      </MarketBanner>

      <div className="flex items-center justify-between mt-6 mb-4">
        <h2 className="heading-30 my-0">Purchase insights</h2>
        <MarketButton
          rank="secondary"
          onClick={() => history.push('/purchasehistory')}
        >
          View purchases
        </MarketButton>
      </div>

      <SummaryTiles stats={insights.summary} />

      <ChartCard title="Spending over time" subtitle="Total spend per month">
        <SpendOverTimeChart
          data={insights.overTime}
          currency={insights.currency}
        />
      </ChartCard>

      {/* US-only feature: the choropleth plots US states, so only surface it
          for buyers whose spend is US-based (USD). */}
      {insights.currency === Currency.USD && (
        <ChartCard
          title="Where you shopped"
          subtitle="Spend by ZIP code area (based on merchant location)"
        >
          <UsSpendMap data={insights.byZip3} currency={insights.currency} />
        </ChartCard>
      )}

      <ChartCard
        title="Spending by category"
        subtitle="By merchant business type"
      >
        <HorizontalBarChart
          data={insights.categories}
          currency={insights.currency}
          emptyText="No merchant category data on these purchases."
        />
      </ChartCard>

      <ChartCard title="Top merchants" subtitle="By total spend">
        <HorizontalBarChart
          data={insights.merchants}
          currency={insights.currency}
        />
      </ChartCard>

      <ChartCard title="Top items" subtitle="By total spend">
        <HorizontalBarChart
          data={insights.items}
          currency={insights.currency}
          emptyText="No itemized line items on these purchases."
        />
      </ChartCard>
    </Page>
  );
};

export default PurchaseInsights;
